import { SigeEinweisung } from '../sige-plan/types';
import { einweisungTitel, formatDatum } from './einweisungLogic';

/**
 * Reine Regeln für die Ablage des Einweisungsblatts in der Projektablage
 * („Dokumente", Stufe 1A des Planungsphasen-Plans). Das Schreiben läuft über
 * `services/unterlagenAblage.ts`; hier steht nur, wann, was und unter welchem
 * Namen.
 *
 * ═══ WANN ABGELEGT WIRD ═══════════════════════════════════════════════════
 * Beim ABSCHLIESSEN (Entscheid E17: Freigabe). Ein abgeschlossenes Blatt ist
 * eingefroren, darunter stehen die Unterschriften — das ist der Nachweis, der
 * zentral liegen muss. Der PDF-Export legt NICHT ab: ein Einweisungsblatt
 * wird mehrfach gedruckt (Aushang, Ordner, Nachzügler), und jeder Druck wäre
 * sonst eine weitere Zeile in der Ablage. Entwürfe werden gar nicht abgelegt;
 * das Blatt ist bis zum Abschluss in Arbeit.
 *
 * ═══ NACHZÜGLER PER QR ════════════════════════════════════════════════════
 * Nach dem Abschluss kann der Link aktiviert werden; wer ihn scannt,
 * unterschreibt online — die Teilnehmerliste wächst also NACH der Ablage
 * weiter. Deshalb trägt die Kennung eines Standes die Zahl der
 * Online-Teilnahmen: „Erneut ablegen" aus der Liste legt dann eine neue
 * Fassung mit den Nachzüglern an, derselbe Stand wird dagegen erkannt und
 * nicht doppelt abgelegt. Online-Zeilen werden nie gelöscht (Nachweis), die
 * Zahl kann also nur wachsen.
 *
 * ═══ PAPIER ═══════════════════════════════════════════════════════════════
 * Bei einer auf Papier gehaltenen Einweisung ist der Scan das Original; das
 * erzeugte PDF wäre ein leeres Blatt mit Kopfdaten. Abgelegt wird deshalb die
 * hinterlegte Datei selbst, unter ihrem ursprünglichen Namen.
 */

/** Was abgelegt wird: das erzeugte Blatt oder der hinterlegte Scan. */
export type EinweisungAblageQuelle = 'blatt' | 'scan';

export const einweisungAblageQuelle = (
    e: Pick<SigeEinweisung, 'quelle' | 'dokument_pfad'>,
): EinweisungAblageQuelle =>
    e.quelle === 'papier' && (e.dokument_pfad || '').trim() ? 'scan' : 'blatt';

/**
 * Nur ein abgeschlossenes Blatt darf in die Ablage; im Lesemodus schreibt
 * das Modul nichts (die Ablage ist eine Zeile und eine Datei im Projekt).
 */
export const darfEinweisungAblegen = (
    e: Pick<SigeEinweisung, 'status'>,
    isReadOnly: boolean,
): boolean => !isReadOnly && e.status === 'abgeschlossen';

/**
 * Die Kennung des Standes (`fassung_ref`).
 *
 * Blatt: Einweisung + Zahl der Online-Teilnahmen (siehe Dateikopf). Die
 * Geräte-Teilnehmer stehen nach dem Abschluss fest und brauchen keine
 * Kennung. Scan: Einweisung + Pfad der Datei — ein neuer Upload ersetzt den
 * Pfad und ist damit ein neuer Stand.
 */
export const einweisungFassungRef = (
    e: Pick<SigeEinweisung, 'id' | 'quelle' | 'dokument_pfad'>,
    onlineAnzahl: number,
): string => {
    if (einweisungAblageQuelle(e) === 'scan') return `einweisung:${e.id}:scan:${(e.dokument_pfad || '').trim()}`;
    return `einweisung:${e.id}:abgeschlossen:online${Math.max(0, onlineAnzahl | 0)}`;
};

/** Dateiname des Blattes — derselbe für Download und Ablage. */
export const einweisungPdfDateiname = (e: Pick<SigeEinweisung, 'nummer' | 'datum'>): string =>
    `Einweisung_${String(e.nummer ?? '').padStart(2, '0')}_${e.datum || ''}.pdf`;

/**
 * Dateiname der abgelegten Datei: beim Scan der ursprüngliche Name (der Pfad
 * im Bucket trägt nur eine UUID), sonst der des Blattes.
 */
export const einweisungAblageDateiname = (
    e: Pick<SigeEinweisung, 'nummer' | 'datum' | 'quelle' | 'dokument_pfad' | 'dokument_name'>,
): string => {
    if (einweisungAblageQuelle(e) === 'scan') {
        const name = (e.dokument_name || '').trim();
        if (name) return name;
    }
    return einweisungPdfDateiname(e);
};

/**
 * Titel der Zeile in der Ablage: Bezeichnung (oder „Einweisung Nr. n"), Tag,
 * und was es ist. Teilnehmer stehen auf dem Blatt; der Titel sagt es nur
 * dazu, damit in der Liste erkennbar bleibt, dass hier Namen liegen.
 */
export const einweisungAblageTitel = (
    e: Pick<SigeEinweisung, 'nummer' | 'titel' | 'datum' | 'quelle' | 'dokument_pfad'>,
): string => {
    const was = einweisungAblageQuelle(e) === 'scan' ? 'Teilnehmerliste (Scan)' : 'Einweisungsblatt mit Teilnehmerliste';
    return `${einweisungTitel(e)} – ${formatDatum(e.datum)} – ${was}`;
};

/** Stand der Zeile: Teilnehmerzahl und Planbezug, falls bekannt. */
export const einweisungAblageStand = (
    e: Pick<SigeEinweisung, 'teilnehmer' | 'plan_nr' | 'plan_stand'>,
    onlineAnzahl: number,
): string => {
    const geraet = (e.teilnehmer || []).length;
    const online = Math.max(0, onlineAnzahl | 0);
    const gesamt = geraet + online;
    const teil = `${gesamt} Teilnehmer${online > 0 ? ` (${online} online)` : ''}`;
    const plan = e.plan_nr || e.plan_stand
        ? ` · Plan ${e.plan_nr ? `Nr. ${e.plan_nr}` : 'ohne Nummer'}${e.plan_stand ? `, Stand ${e.plan_stand}` : ''}`
        : '';
    return `${teil}${plan}`;
};

/**
 * Warnsatz bei `schon_abgelegt` — oder `null`. Der Kern meldet dort nur dann
 * einen `fehler`, wenn die doppelt hochgeladene Datei nicht wieder entfernt
 * werden konnte (`verwaisteDatei`): die Fassung liegt, aber im Bucket blieb
 * eine Datei ohne Zeile. Das muss sichtbar sein; ein Retry hilft nicht.
 */
export const einweisungAblageWarnung = (ergebnis: {
    status: 'abgelegt' | 'schon_abgelegt' | 'fehler';
    fehler: string | null;
    verwaisteDatei?: string | null;
}): string | null => {
    if (ergebnis.status !== 'schon_abgelegt') return null;
    const satz = (ergebnis.fehler || '').trim();
    const pfad = (ergebnis.verwaisteDatei || '').trim();
    if (!satz && !pfad) return null;
    const kern = satz || 'Eine doppelt hochgeladene Datei konnte nicht wieder entfernt werden.';
    return pfad ? `${kern} Verwaiste Datei: ${pfad}` : kern;
};

/** Beschreibung: Anlass und Herkunft der Zeile. */
export const einweisungAblageBeschreibung = (
    e: Pick<SigeEinweisung, 'quelle' | 'dokument_pfad' | 'durchfuehrender_name' | 'ort'>,
    a: { vonHand: boolean },
): string => {
    const woher = a.vonHand ? 'Aus der Einweisungsliste abgelegt' : 'Beim Abschließen der Einweisung abgelegt';
    const art = einweisungAblageQuelle(e) === 'scan'
        ? 'Auf Papier durchgeführt; hinterlegter Scan.'
        : 'Digital geführt und unterschrieben.';
    const wer = (e.durchfuehrender_name || '').trim();
    const ort = (e.ort || '').trim();
    const zusatz = [wer ? `Durchführender: ${wer}` : '', ort ? `Ort: ${ort}` : ''].filter(Boolean).join(', ');
    return `${woher}. ${art}${zusatz ? ` ${zusatz}.` : ''}`;
};
